import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { history, useParams } from 'umi';
import type { IKpiEventFilterLanguage, IChartEventSetting } from '@/pages/dashboard/filterPageComponents/overview/KpiEventFilter';
import type {
  EventBubbleDetailsNavConfig,
  TEventTypeBarColorMapConfig,
} from '@/pages/dashboard/components/EventMetricTrend/types';
import { eventAxisValueToQueryDate } from '@/pages/dashboard/components/EventMetricTrend/utils';
import { useLinkPath } from '@/pages/dashboard/utils/customHook';
import {
  buildEventModalDataScopeKey,
  buildFormValueScopeKey,
  buildFullScreenEventData,
  buildPreCollapseEventCountByAxis,
  convertEventDataToModalData,
  normalizeDateForCompare,
  resolveFullScreenEventSource,
  resolveFullScreenExcludeEventTypes,
  resolveOverviewEventDateFormat,
  resolveOverviewEventDateType,
  resolvePreCollapseEventCount,
  type TOverviewEventDateType,
} from './eventModalDataUtils';
import type { IFullScreenEnhancements } from './index';

const createDefaultFsChartEventSetting = (): IChartEventSetting => ({
  showEventAxis: false,
  showEventBubbles: true,
  bubbleEvent: {},
  showExperimentEventDetailFlat: false,
});

/** 是否保留全屏事件设置；仅显式 false / "false" 为不保留 */
function resolvePersistEventSetting(
  value: boolean | string | undefined,
): boolean {
  if (value === false || value === 'false') {
    return false;
  }
  return true;
}

interface IUseFsEventEnhancementsParams {
  fullScreenEnhancements: IFullScreenEnhancements | undefined;
  eventTypeMaxCountCfg: number | undefined;
  eventData: unknown[];
  data: Record<string, unknown> | undefined;
  formValue: Record<string, unknown> | undefined;
}

export interface IUseFsEventEnhancementsResult {
  // 配置解析
  fsShowEventFilter: boolean;
  fsShowEventWindows: boolean;
  fsShowSegmentTabsInFullScreen: boolean;
  fsEventFilterBesideTitle: boolean;
  fsPersistEventSetting: boolean;
  eventTypeMaxCount: number;
  fsEventFilterLanguage: Partial<IKpiEventFilterLanguage> | undefined;
  fsEventTypeBarColorMap: TEventTypeBarColorMapConfig | undefined;
  /** 必须由父组件持有的 ref，Portal remount 时才能安全保留全屏上一帧状态 */
  prevViewportFullScreenRef: React.MutableRefObject<boolean>;
  // 事件详情导航
  eventBubbleDetailsNavCfg: EventBubbleDetailsNavConfig | undefined;
  eventDetailsPagePath: string;
  // 日期相关
  fsEventDateType: TOverviewEventDateType;
  fsEventDateFormat: string;
  // X 轴 / 事件数据
  fsXAxisData: string[];
  handleFsXAxisDataChange: (xAxis: string[]) => void;
  fullScreenEventData: unknown[];
  fsEventModalData: Record<string, unknown[]>;
  preCollapseEventCountByAxis: Record<string, number>;
  eventDetailsNavForWindows:
    | {
        minCountExclusive: number;
        onNavigate: (item: unknown, xAxisValue: string) => void;
        shouldAllowDetailsNav: (xAxisValue: string) => boolean;
      }
    | undefined;
  // KpiEventFilter 状态
  fsChartEventSetting: IChartEventSetting;
  fsBubbleFilterCommitted: boolean;
  fsEventSettingResetKey: number;
  handleFsEventSettingChange: (next: IChartEventSetting) => void;
  resetFsEventSetting: () => void;
}

/**
 * 全屏增强逻辑：解析 fullScreenEnhancements 配置、管理 KpiEventFilter 状态、
 * 构建事件数据、事件浮窗导航配置等。
 *
 * 将此 Hook 从 OverviewMetricsTrendCo 中拆出，保持组件体职责单一。
 * prevViewportFullScreenRef 由本 Hook 内部持有，通过返回值透出给组件——
 * 组件需将其传给 FsEventSettingResetGuard，以保证 Portal remount 时 ref 不丢失。
 */
export function useFsEventEnhancements(
  params: IUseFsEventEnhancementsParams,
): IUseFsEventEnhancementsResult {
  const { fullScreenEnhancements, eventTypeMaxCountCfg, eventData, data, formValue } = params;

  // ── 配置解析 ──────────────────────────────────────────────────────────────
  const fsShowEventFilter = fullScreenEnhancements?.showEventFilter !== false;
  const fsShowEventWindows = fullScreenEnhancements?.showEventWindows !== false;
  const fsShowSegmentTabsInFullScreen =
    fullScreenEnhancements?.showSegmentTabsInFullScreen === true;
  const fsEventFilterBesideTitle =
    fullScreenEnhancements?.eventFilterBesideTitle === true;
  const fsExcludeEventTypes = useMemo(
    () => resolveFullScreenExcludeEventTypes(fullScreenEnhancements),
    [fullScreenEnhancements],
  );
  const fsPersistEventSetting = resolvePersistEventSetting(
    fullScreenEnhancements?.persistEventSetting,
  );
  const eventTypeMaxCount = useMemo(() => {
    if (typeof eventTypeMaxCountCfg === 'number') {
      return eventTypeMaxCountCfg;
    }
    const legacy = fullScreenEnhancements?.eventTypeMaxCount;
    if (typeof legacy === 'number') {
      return legacy;
    }
    return 3;
  }, [eventTypeMaxCountCfg, fullScreenEnhancements?.eventTypeMaxCount]);
  const fsEventFilterLanguage = fullScreenEnhancements?.eventFilterLanguage;
  const fsEventModalDataKey = fullScreenEnhancements?.eventModalDataKey;
  const eventBubbleDetailsNavCfg = fullScreenEnhancements?.eventBubbleDetailsNav;
  const fsEventTypeBarColorMap = fullScreenEnhancements?.eventTypeBarColorMap;

  // ── 全屏上一帧 ref（必须稳定持有，Portal remount 时不能丢失） ──────────────
  const prevViewportFullScreenRef = useRef(false);

  // ── 事件详情导航路径 ──────────────────────────────────────────────────────
  const routeParams = useParams() as { type?: string; gamecode?: string; };
  const eventDetailsLinkPath = useLinkPath(
    eventBubbleDetailsNavCfg?.path ?? 'eventAnalysis/eventDetails',
  ) as string;
  const eventDetailsPagePath = useMemo(() => {
    if (!eventBubbleDetailsNavCfg) {
      return '';
    }
    const { type, gamecode } = routeParams;
    if (!type || !gamecode) {
      return '';
    }
    return eventDetailsLinkPath;
  }, [eventBubbleDetailsNavCfg, eventDetailsLinkPath, routeParams]);

  // ── 日期格式 ─────────────────────────────────────────────────────────────
  const timeType = formValue?.timeType as string | undefined;
  const fsEventDateType = useMemo(
    () => resolveOverviewEventDateType(timeType),
    [timeType],
  );
  const fsEventDateFormat = useMemo(
    () => resolveOverviewEventDateFormat(timeType),
    [timeType],
  );

  // ── X 轴状态 ─────────────────────────────────────────────────────────────
  /** 图表 X 轴最小刻度（由 FsEventWindows 回传，用于事件源 start_time 裁剪） */
  const [fsTrendMinAxisDate, setFsTrendMinAxisDate] = useState('');
  const [fsXAxisData, setFsXAxisData] = useState<string[]>([]);
  const handleFsXAxisDataChange = useCallback((xAxis: string[]) => {
    setFsXAxisData(xAxis);
    setFsTrendMinAxisDate(normalizeDateForCompare(xAxis[0] ?? ''));
  }, []);

  // ── 事件数据 ─────────────────────────────────────────────────────────────
  const fsFilterStartDateFallback = useMemo(() => {
    const raw =
      formValue?.start_date ??
      (formValue?.filterValue as Record<string, unknown> | undefined)?.start_date;
    return normalizeDateForCompare(String(raw ?? ''));
  }, [formValue]);

  /**
   * 全屏事件统一数据源：先 resolve 源 → exclude + start_time 过滤 → 再分给弹窗/浮窗。
   */
  const fullScreenEventData = useMemo(() => {
    const source = resolveFullScreenEventSource(
      eventData,
      data,
      fsEventModalDataKey,
    );
    return buildFullScreenEventData(source, {
      excludeSet: fsExcludeEventTypes,
      minAxisDate: fsTrendMinAxisDate || fsFilterStartDateFallback,
    });
  }, [
    data,
    eventData,
    fsEventModalDataKey,
    fsExcludeEventTypes,
    fsTrendMinAxisDate,
    fsFilterStartDateFallback,
  ]);

  const fsEventModalData = useMemo(
    () => convertEventDataToModalData(fullScreenEventData),
    [fullScreenEventData],
  );

  // ── KpiEventFilter 本地状态 ───────────────────────────────────────────────
  /**
   * 全屏内 KpiEventFilter 的本地状态：仅在全屏时被消费，独立于 chartItem 内的事件浮窗逻辑。
   * 不影响非全屏行为；不联动 chartItem 内 X 轴事件气泡。
   */
  const [fsChartEventSetting, setFsChartEventSetting] = useState<IChartEventSetting>(
    createDefaultFsChartEventSetting,
  );
  /** 弹窗已点过「确定」写入 bubbleEvent 后为 true，用于区分「未配置」与「全不选」 */
  const [fsBubbleFilterCommitted, setFsBubbleFilterCommitted] = useState(false);
  /** persistEventSetting=false 退出全屏时递增，强制 remount KpiEventFilter 清掉弹窗/Filter 内部态 */
  const [fsEventSettingResetKey, setFsEventSettingResetKey] = useState(0);

  const handleFsEventSettingChange = useCallback((next: IChartEventSetting) => {
    setFsChartEventSetting(prev => {
      if (next.bubbleEvent !== prev.bubbleEvent) {
        setFsBubbleFilterCommitted(true);
      }
      return next;
    });
  }, []);

  const resetFsEventSetting = useCallback(() => {
    setFsChartEventSetting(createDefaultFsChartEventSetting());
    setFsBubbleFilterCommitted(false);
    setFsEventSettingResetKey(prev => prev + 1);
  }, []);

  /** 筛选器或事件源变化时重置 bubble 勾选，避免旧 start_time 的 key 筛掉新时段事件 */
  const fsFormValueScopeKey = useMemo(
    () => buildFormValueScopeKey(formValue),
    [formValue],
  );
  const fsEventModalDataScopeKey = useMemo(
    () => buildEventModalDataScopeKey(fsEventModalData),
    [fsEventModalData],
  );
  const fsEventScopePrevRef = useRef<{
    form: string;
    eventData: string;
  } | null>(null);

  useEffect(() => {
    const nextScope = {
      form: fsFormValueScopeKey,
      eventData: fsEventModalDataScopeKey,
    };
    if (fsEventScopePrevRef.current === null) {
      fsEventScopePrevRef.current = nextScope;
      return;
    }
    const prevScope = fsEventScopePrevRef.current;
    if (
      prevScope.form === nextScope.form &&
      prevScope.eventData === nextScope.eventData
    ) {
      return;
    }
    fsEventScopePrevRef.current = nextScope;
    setFsBubbleFilterCommitted(false);
    setFsChartEventSetting(prev => ({
      ...prev,
      bubbleEvent: {},
    }));
    setFsEventSettingResetKey(prev => prev + 1);
  }, [fsFormValueScopeKey, fsEventModalDataScopeKey]);

  // ── 事件气泡跳转 ─────────────────────────────────────────────────────────
  const handleEventBubbleDetailsNavigate = useCallback(
    (item: unknown, xAxisValue: string) => {
      if (!eventBubbleDetailsNavCfg || !eventDetailsPagePath) {
        return;
      }
      const row = item as
        | {
            event_type_id?: string | number;
            event_type?: { id?: string | number; };
          }
        | null
        | undefined;
      const date = eventAxisValueToQueryDate(xAxisValue, {
        dateType: fsEventDateType,
      });
      const et = row?.event_type_id ?? row?.event_type?.id;
      const query: Record<string, string> = {
        date,
        dashboardType: 'overview',
        fromPage: history.location.pathname,
        ...(eventBubbleDetailsNavCfg.query ?? {}),
        // timeType 放在 query 展开之后，防止 pageConfig 静态 query.timeType 覆盖动态粒度
        timeType: String(formValue?.timeType || 'Day'),
      };
      if (et !== undefined && et !== null && String(et) !== '') {
        query.eventType = String(et);
      }
      history.push({ pathname: eventDetailsPagePath, query });
    },
    [eventBubbleDetailsNavCfg, eventDetailsPagePath, formValue, fsEventDateType],
  );

  // ── 事件计数与导航配置 ────────────────────────────────────────────────────
  const preCollapseEventCountByAxis = useMemo(
    () =>
      buildPreCollapseEventCountByAxis(
        fullScreenEventData as unknown[],
        fsChartEventSetting.bubbleEvent ?? {},
        fsBubbleFilterCommitted,
      ),
    [fullScreenEventData, fsChartEventSetting.bubbleEvent, fsBubbleFilterCommitted],
  );

  const eventDetailsNavForWindows = useMemo(() => {
    if (!eventBubbleDetailsNavCfg) {
      return undefined;
    }
    /**
     * 概览全屏默认 0：当日 ≥1 条即可点跳（与非全屏 EventDetailsPop 一致）。
     * KPI EventMetricTrend 仍为 3（至少 4 条才可点）。
     */
    const minCountExclusive = eventBubbleDetailsNavCfg.minCountExclusive ?? 0;
    return {
      minCountExclusive,
      onNavigate: handleEventBubbleDetailsNavigate,
      shouldAllowDetailsNav: (xAxisValue: string): boolean =>
        resolvePreCollapseEventCount(
          preCollapseEventCountByAxis,
          xAxisValue,
          { dateType: fsEventDateType },
        ) > minCountExclusive,
    };
  }, [
    eventBubbleDetailsNavCfg,
    fsEventDateType,
    handleEventBubbleDetailsNavigate,
    preCollapseEventCountByAxis,
  ]);

  return {
    fsShowEventFilter,
    fsShowEventWindows,
    fsShowSegmentTabsInFullScreen,
    fsEventFilterBesideTitle,
    fsPersistEventSetting,
    eventTypeMaxCount,
    fsEventFilterLanguage,
    fsEventTypeBarColorMap,
    prevViewportFullScreenRef,
    eventBubbleDetailsNavCfg,
    eventDetailsPagePath,
    fsEventDateType,
    fsEventDateFormat,
    fsXAxisData,
    handleFsXAxisDataChange,
    fullScreenEventData,
    fsEventModalData,
    preCollapseEventCountByAxis,
    eventDetailsNavForWindows,
    fsChartEventSetting,
    fsBubbleFilterCommitted,
    fsEventSettingResetKey,
    handleFsEventSettingChange,
    resetFsEventSetting,
  };
}
